import { supabase } from "@/config/supabase";
import { Booking, BookingFilters, BookingStatus } from "@/types";
import { SUPABASE_TABLES } from "@/config/constants";
import { errorLogger, ErrorSeverity } from "./errorLogger.service";
import { validateStatusTransition, StatusTransitionError } from "@/utils/bookingStateMachine";

// Re-export for convenience
export { StatusTransitionError } from "@/utils/bookingStateMachine";

/**
 * Custom error class for booking conflicts (double-booking prevention)
 */
export class BookingConflictError extends Error {
  constructor(
    message: string = "This time slot has just been booked by another customer. Please select a different time."
  ) {
    super(message);
    this.name = "BookingConflictError";
  }
}

const BOOKING_COLUMNS =
  "id,referenceCode,customerId,customerEmail,customerName,customerPhone,serviceId,packageId,addonIds,scheduledDate,scheduledTime,timeSlotId,propertyAddress,propertyType,specialInstructions,mlsNumber,subtotal,discount,discountReason,couponId,couponCode,tax,total,status,paymentStatus,paymentMethod,stripePaymentIntentId,approvedBy,approvedAt,deniedReason,createdAt,updatedAt,completedAt,deliverableDropboxLink,deliverableEmailSentAt";

const convertTimestamps = (data: any): any => {
  const converted = { ...data };
  if (converted.scheduledDate?.toDate) {
    converted.scheduledDate = converted.scheduledDate.toDate();
  }
  if (converted.createdAt?.toDate) {
    converted.createdAt = converted.createdAt.toDate();
  }
  if (converted.updatedAt?.toDate) {
    converted.updatedAt = converted.updatedAt.toDate();
  }
  if (converted.completedAt?.toDate) {
    converted.completedAt = converted.completedAt.toDate();
  }
  if (converted.approvedAt?.toDate) {
    converted.approvedAt = converted.approvedAt.toDate();
  }
  if (converted.deliverableEmailSentAt?.toDate) {
    converted.deliverableEmailSentAt = converted.deliverableEmailSentAt.toDate();
  }
  return converted;
};

export const createBooking = async (
  booking: Omit<Booking, "id">,
  packageName?: string,
  serviceName?: string
): Promise<{ id: string; referenceCode?: string }> => {
  if (!booking.customerId) {
    throw new Error("Authenticated user is required to create a booking");
  }

  // Use atomic database function to prevent race conditions
  // This performs availability check and insert in a single transaction
  const { data, error } = await supabase.rpc("create_booking_atomic", {
    p_customer_id: booking.customerId,
    p_customer_email: booking.customerEmail,
    p_customer_name: booking.customerName,
    p_customer_phone: booking.customerPhone,
    p_service_id: booking.serviceId,
    p_package_id: booking.packageId,
    p_addon_ids: booking.addonIds || [],
    p_scheduled_date: booking.scheduledDate.toISOString(),
    p_scheduled_time: booking.scheduledTime,
    p_time_slot_id: booking.timeSlotId,
    p_property_address: booking.propertyAddress,
    p_property_type: booking.propertyType,
    p_special_instructions: booking.specialInstructions || null,
    p_mls_number: booking.mlsNumber || null,
    p_subtotal: booking.subtotal,
    p_discount: booking.discount ?? 0,
    p_discount_reason: booking.discountReason || null,
    p_tax: booking.tax,
    p_total: booking.total,
    p_status: booking.status,
    p_payment_status: booking.paymentStatus,
    p_stripe_payment_intent_id: booking.stripePaymentIntentId || null,
    p_coupon_id: booking.couponId || null,
    p_coupon_code: booking.couponCode || null,
    p_payment_method: booking.paymentMethod,
  });

  if (error) {
    // Handle unique constraint violation from direct insert (fallback)
    if (error.code === "23505" && error.message?.includes("idx_bookings_unique_active_timeslot")) {
      throw new BookingConflictError();
    }
    throw new Error(error.message);
  }

  // Handle conflict detected by atomic function
  const result = data?.[0];
  if (result?.is_conflict) {
    throw new BookingConflictError(result.conflict_message);
  }

  if (!result?.id) {
    throw new Error("Failed to create booking: no ID returned");
  }

  const bookingId = result.id;
  const referenceCode = result.reference_code;

  // Send email notifications asynchronously (don't block booking creation)
  if (packageName && serviceName) {
    const { emailService } = await import("./email.service");
    const bookingWithId = {
      ...booking,
      id: bookingId,
      referenceCode: referenceCode as string | undefined,
    };

    // Send confirmation to customer
    emailService.sendBookingConfirmation(bookingWithId, packageName, serviceName).catch((err) => {
      errorLogger.logError(err, ErrorSeverity.WARNING, {
        action: "sendBookingConfirmation",
        component: "booking.service",
        metadata: { bookingId },
      });
    });

    // Send notification to admin
    emailService
      .sendAdminNewBookingNotification(bookingWithId, packageName, serviceName)
      .catch((err) => {
        errorLogger.logError(err, ErrorSeverity.WARNING, {
          action: "sendAdminNewBookingNotification",
          component: "booking.service",
          metadata: { bookingId },
        });
      });
  }

  return {
    id: bookingId,
    referenceCode: referenceCode as string | undefined,
  };
};

/**
 * Fallback booking creation using direct insert
 * Used when the atomic function is not available (e.g., older database)
 * Still protected by the unique index constraint
 */
export const createBookingDirect = async (
  booking: Omit<Booking, "id">,
  packageName?: string,
  serviceName?: string
): Promise<{ id: string; referenceCode?: string }> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .insert({
      ...booking,
      scheduledDate: booking.scheduledDate.toISOString(),
      createdAt: booking.createdAt.toISOString(),
      updatedAt: booking.updatedAt.toISOString(),
    })
    .select("id, referenceCode")
    .single();

  if (error) {
    // Handle unique constraint violation (double booking attempt)
    if (error.code === "23505" && error.message?.includes("idx_bookings_unique_active_timeslot")) {
      throw new BookingConflictError();
    }
    throw new Error(error.message);
  }

  // Send email notifications asynchronously (don't block booking creation)
  if (packageName && serviceName) {
    const { emailService } = await import("./email.service");
    const bookingWithId = {
      ...booking,
      id: data.id,
      referenceCode: (data as any).referenceCode as string | undefined,
    };

    // Send confirmation to customer
    emailService.sendBookingConfirmation(bookingWithId, packageName, serviceName).catch((err) => {
      errorLogger.logError(err, ErrorSeverity.WARNING, {
        action: "sendBookingConfirmation",
        component: "booking.service",
        metadata: { bookingId: data.id },
      });
    });

    // Send notification to admin
    emailService
      .sendAdminNewBookingNotification(bookingWithId, packageName, serviceName)
      .catch((err) => {
        errorLogger.logError(err, ErrorSeverity.WARNING, {
          action: "sendAdminNewBookingNotification",
          component: "booking.service",
          metadata: { bookingId: data.id },
        });
      });
  }

  return {
    id: data.id as string,
    referenceCode: (data as any).referenceCode as string | undefined,
  };
};

export const getBookingById = async (id: string): Promise<Booking | null> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select(BOOKING_COLUMNS)
    .eq("id", id)
    .single();

  if (error || !data) return null;
  const converted = convertTimestamps({
    ...data,
    scheduledDate: new Date(data.scheduledDate),
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    deliverableEmailSentAt: data.deliverableEmailSentAt
      ? new Date(data.deliverableEmailSentAt)
      : null,
  });
  return { id: data.id, ...converted } as Booking;
};

export const getBookingsByCustomerId = async (customerId: string): Promise<Booking[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select(BOOKING_COLUMNS)
    .eq("customerId", customerId)
    .order("createdAt", { ascending: false });
  if (error || !data) return [];
  return data.map((row) => {
    const converted = convertTimestamps({
      ...row,
      scheduledDate: new Date(row.scheduledDate),
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deliverableEmailSentAt: row.deliverableEmailSentAt
        ? new Date(row.deliverableEmailSentAt)
        : null,
    });
    return { id: row.id, ...converted } as Booking;
  });
};

export const getAllBookings = async (filters?: BookingFilters): Promise<Booking[]> => {
  let query = supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select(BOOKING_COLUMNS)
    .order("createdAt", { ascending: false });

  if (filters?.customerId) {
    query = query.eq("customerId", filters.customerId);
  }
  if (filters?.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }

  const { data, error } = await query;
  if (error || !data) return [];
  return data.map((row) => {
    const converted = convertTimestamps({
      ...row,
      scheduledDate: new Date(row.scheduledDate),
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deliverableEmailSentAt: row.deliverableEmailSentAt
        ? new Date(row.deliverableEmailSentAt)
        : null,
    });
    return { id: row.id, ...converted } as Booking;
  });
};

export const updateBooking = async (
  id: string,
  updates: Partial<Booking>,
  serviceName?: string
): Promise<void> => {
  // Get current booking to check for status changes
  const currentBooking = await getBookingById(id);

  if (!currentBooking) {
    throw new Error(`Booking not found: ${id}`);
  }

  const oldStatus = currentBooking.status;
  const newStatus = updates.status;

  // ✅ Validate status transition using state machine
  if (newStatus && oldStatus !== newStatus) {
    try {
      validateStatusTransition(oldStatus, newStatus as BookingStatus);
    } catch (error) {
      if (error instanceof StatusTransitionError) {
        // Re-throw with enhanced context for the UI
        throw error;
      }
      throw error;
    }
  }

  const updateData: any = { ...updates, updatedAt: new Date().toISOString() };
  if (updates.scheduledDate) {
    updateData.scheduledDate = updates.scheduledDate.toISOString();
  }
  if (updates.completedAt) {
    updateData.completedAt = updates.completedAt.toISOString();
  }

  // Set completedAt timestamp when transitioning to completed
  if (newStatus === "completed" && !updateData.completedAt) {
    updateData.completedAt = new Date().toISOString();
  }

  const { error } = await supabase.from(SUPABASE_TABLES.BOOKINGS).update(updateData).eq("id", id);
  if (error) throw new Error(error.message);

  // Send email notification if status changed
  if (oldStatus && newStatus && oldStatus !== newStatus && currentBooking && serviceName) {
    const { emailService } = await import("./email.service");
    const updatedBooking = { ...currentBooking, ...updates, id };

    emailService
      .sendBookingStatusUpdate(updatedBooking, oldStatus, newStatus, serviceName)
      .catch((err) => {
        errorLogger.logError(err, ErrorSeverity.WARNING, {
          action: "sendBookingStatusUpdate",
          component: "booking.service",
          metadata: { bookingId: id, oldStatus, newStatus },
        });
      });

    // Send review request when booking is completed
    if (newStatus === "completed") {
      emailService
        .sendReviewRequest(
          currentBooking.customerEmail,
          currentBooking.customerName,
          serviceName,
          currentBooking.referenceCode
        )
        .catch((err) => {
          errorLogger.logError(err, ErrorSeverity.WARNING, {
            action: "sendReviewRequest",
            component: "booking.service",
            metadata: { bookingId: id },
          });
        });
    }
  }
};

export const getUpcomingBookings = async (limit = 10): Promise<Booking[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select(BOOKING_COLUMNS)
    .gte("scheduledDate", new Date().toISOString())
    .in("status", ["confirmed", "pending-approval", "approved"])
    .order("scheduledDate", { ascending: true })
    .limit(limit);
  if (error || !data) return [];
  return data.map((row) => {
    const converted = convertTimestamps({
      ...row,
      scheduledDate: new Date(row.scheduledDate),
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
    });
    return { id: row.id, ...converted } as Booking;
  });
};

// Approve booking (with state machine validation)
export const approveBooking = async (id: string, approvedBy?: string | null): Promise<void> => {
  // Get current booking to validate transition
  const currentBooking = await getBookingById(id);
  if (!currentBooking) {
    throw new Error(`Booking not found: ${id}`);
  }

  // ✅ Validate transition: only pending-approval → approved is valid
  validateStatusTransition(currentBooking.status, "approved");

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  let approvedByUserId: string | null | undefined = approvedBy;

  if (approvedByUserId && !UUID_REGEX.test(approvedByUserId)) {
    approvedByUserId = null;
  }

  if (!approvedByUserId) {
    const { data: sessionData } = await supabase.auth.getSession();
    const sessionUserId = sessionData.session?.user?.id;
    approvedByUserId = sessionUserId && UUID_REGEX.test(sessionUserId) ? sessionUserId : null;
  }

  const { error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .update({
      status: "approved",
      approvedBy: approvedByUserId,
      approvedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw new Error(error.message);
};

// Deny booking (with state machine validation)
export const denyBooking = async (id: string, reason: string): Promise<void> => {
  if (!reason?.trim()) {
    throw new Error("A reason is required when denying a booking");
  }

  // Get current booking to validate transition
  const currentBooking = await getBookingById(id);
  if (!currentBooking) {
    throw new Error(`Booking not found: ${id}`);
  }

  // ✅ Validate transition: only pending-approval → denied is valid
  validateStatusTransition(currentBooking.status, "denied");

  const { error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .update({
      status: "denied",
      deniedReason: reason.trim(),
      updatedAt: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw new Error(error.message);
};

// Cancel booking (with state machine validation)
export const cancelBooking = async (id: string, reason: string): Promise<void> => {
  if (!reason?.trim()) {
    throw new Error("A reason is required when cancelling a booking");
  }

  // Get current booking to validate transition
  const currentBooking = await getBookingById(id);
  if (!currentBooking) {
    throw new Error(`Booking not found: ${id}`);
  }

  // ✅ Validate transition using state machine
  validateStatusTransition(currentBooking.status, "cancelled");

  const { error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .update({
      status: "cancelled",
      deniedReason: reason.trim(), // Reuse deniedReason for cancellation reason
      updatedAt: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw new Error(error.message);
};

// Check if time slot is available with travel buffer consideration
export const checkTimeSlotAvailability = async (
  date: Date,
  timeSlotId: string,
  travelBufferMinutes: number = 30
): Promise<boolean> => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select("id,timeSlotId,scheduledDate,status")
    .gte("scheduledDate", startOfDay.toISOString())
    .lte("scheduledDate", endOfDay.toISOString())
    .in("status", ["pending-approval", "approved", "confirmed", "in-progress"]);
  if (error) return true;
  const bookings = ((data as any[]) || []).map((row: any) => ({
    id: row.id,
    timeSlotId: row.timeSlotId,
    scheduledDate: new Date(row.scheduledDate),
    status: row.status,
  }));

  // Parse the requested time slot
  const [, requestedTimeStr] = timeSlotId.split("-");
  const [requestedHour, requestedMin] = requestedTimeStr.split(":").map(Number);
  const requestedTimeMinutes = requestedHour * 60 + requestedMin;

  // Check if any existing booking conflicts with the requested slot + buffer
  for (const booking of bookings) {
    const [, bookedTimeStr] = booking.timeSlotId.split("-");
    const [bookedHour, bookedMin] = bookedTimeStr.split(":").map(Number);
    const bookedTimeMinutes = bookedHour * 60 + bookedMin;

    // Calculate the time difference
    const timeDifference = Math.abs(requestedTimeMinutes - bookedTimeMinutes);

    // If within travel buffer, it's a conflict
    // Note: timeDifference > 0 excludes exact matches (already checked above)
    if (timeDifference < travelBufferMinutes && timeDifference > 0) {
      return false;
    }
  }

  return true;
};

// Get bookings for date range
export const getBookingsByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<Booking[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.BOOKINGS)
    .select(BOOKING_COLUMNS)
    .gte("scheduledDate", startDate.toISOString())
    .lte("scheduledDate", endDate.toISOString())
    .order("scheduledDate", { ascending: true });
  if (error || !data) return [];
  return (data as any[]).map((row: any) => {
    const converted = convertTimestamps({
      ...row,
      scheduledDate: new Date(row.scheduledDate),
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
    });
    return { id: row.id, ...converted } as Booking;
  });
};

// Alias for getBookingsByCustomerId
export const getBookingsByCustomer = getBookingsByCustomerId;

export const deleteBooking = async (id: string): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.BOOKINGS).delete().eq("id", id);

  if (error) throw new Error(error.message);
};

/**
 * Issue a full or partial refund for a booking
 * @param bookingId - Booking ID to refund
 * @param amount - Refund amount (optional, defaults to full booking total)
 * @param reason - Reason for issuing the refund
 * @returns Refund details from Stripe
 */
export const issueRefund = async (
  bookingId: string,
  amount?: number,
  reason?: string
): Promise<{
  success: boolean;
  refund: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created: number;
    isPartial: boolean;
  };
  booking: {
    id: string;
    paymentStatus: string;
    refundAmount: number;
    refundReason?: string;
  };
}> => {
  try {
    // Get auth token
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error("Not authenticated");
    }

    // Call the refund API
    const response = await fetch("/api/stripe-refund", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        bookingId,
        amount,
        reason,
      }),
    });

    // Handle rate limiting with user-friendly message
    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || "Failed to process refund");
    }

    return data;
  } catch (error) {
    errorLogger.logError(error instanceof Error ? error : String(error), ErrorSeverity.ERROR, {
      action: "issueRefund",
      component: "booking.service",
      metadata: { bookingId, amount, reason },
    });
    throw error;
  }
};
